import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { ConsoleSidebarBrand, ConsoleSidebarEndorsement } from '../../lib/console-sidebar-brand';
import { useAuth } from '../../lib/auth';
import { dashboardPath, setLastLearnerId } from '../lib/selected-learner';

/**
 * Parent portal frame.
 *
 * Reuses the `.console` layout from the staff console — same grid, same drawer,
 * same 60rem breakpoint — with different nav contents. Two shells that behave
 * identically means one set of rules to maintain, and a parent moving between
 * screens never relearns the frame.
 *
 * The children come first in the nav because they are what a parent came for;
 * everything else here is in service of one of them. Each carries its status so
 * a parent with three children can see which one needs them without opening
 * anything.
 */

interface ChildNavItem {
  learnerId: string;
  firstName: string;
  lastName: string;
  applicationStatus: string;
}

export interface ParentShellProps {
  children: ReactNode;
  /** Rendered in the top bar. */
  title?: string;
  headerActions?: ReactNode;
  /** Drives the sidebar list. Pass the result of the learners query. */
  learners?: ChildNavItem[];
}

/** Colour is a secondary cue here — the status is always written out too. */
type Tone = 'success' | 'pending' | 'info' | 'neutral';

const TONES: Record<string, Tone> = {
  ADMISSION_CONFIRMED: 'success',
  ADMISSION_PROVISIONAL: 'success',
  PAID: 'success',
  REGISTRATION_PENDING_PAYMENT: 'pending',
  PAYMENT_PENDING: 'pending',
  QUOTATION_ISSUED: 'pending',
  APPROVAL_COMPLETE: 'pending',
  AWAITING_PARENT_DECISION: 'pending',
  CHANGES_REQUESTED: 'pending',
  UNDER_REVIEW: 'info',
  REGISTERED: 'info',
  SUBMITTED: 'info',
};

/** Short enough to fit the sidebar without truncating. */
const SHORT_STATUS: Record<string, string> = {
  ADMISSION_CONFIRMED: 'Confirmed',
  ADMISSION_PROVISIONAL: 'Fee paid',
  PAID: 'Paid',
  REGISTRATION_PENDING_PAYMENT: 'Fee due',
  PAYMENT_PENDING: 'Fees due',
  QUOTATION_ISSUED: 'Fees due',
  APPROVAL_COMPLETE: 'Fees due',
  AWAITING_PARENT_DECISION: 'Needs you',
  CHANGES_REQUESTED: 'Needs you',
  UNDER_REVIEW: 'Under review',
  REGISTERED: 'Choose subjects',
  DRAFT: 'Incomplete',
  REJECTED: 'Not approved',
};

function initials(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function Icon({ d }: { d: string }): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
         dangerouslySetInnerHTML={{ __html: d }} />
  );
}

const ICON = {
  card: '<rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/>',
  user: '<circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0116 0"/>',
  help: '<circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 115 .5c0 1.5-2.5 2-2.5 3.5M12 17h.01"/>',
};

export function ParentShell({
  children,
  title,
  headerActions,
  learners = [],
}: ParentShellProps): ReactElement {
  const { me, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const childMatch = location.pathname.match(/^\/children\/([^/]+)/);
  const brandTo = dashboardPath(childMatch?.[1] ?? null);

  // Navigating from inside the drawer should dismiss it, otherwise it sits
  // over the page the parent just asked for.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    <div className="console" data-drawer={drawerOpen ? 'open' : 'closed'}>
      <aside className="console-sidebar" id="parent-nav">
        <ConsoleSidebarBrand to={brandTo} />

        <nav className="console-nav" aria-label="Your account">
          <div className="console-nav-group">
            <p className="console-nav-label">Your children</p>

            {learners.map((learner) => {
              const tone = TONES[learner.applicationStatus] ?? 'neutral';
              const short = SHORT_STATUS[learner.applicationStatus] ?? 'In progress';
              return (
                <NavLink
                  key={learner.learnerId}
                  to={`/children/${learner.learnerId}`}
                  className={({ isActive }) => `child-nav-link${isActive ? ' is-active' : ''}`}
                  onClick={() => setLastLearnerId(learner.learnerId)}
                >
                  <span className="child-avatar" aria-hidden="true">
                    {initials(learner.firstName, learner.lastName)}
                  </span>
                  <span className="child-nav-text">
                    <span className="child-nav-name">
                      {learner.firstName} {learner.lastName}
                    </span>
                    <span className="child-nav-status">{short}</span>
                  </span>
                  {/* Decorative: the status is already stated above it. */}
                  <span className={`child-nav-dot dot-${tone}`} aria-hidden="true" />
                </NavLink>
              );
            })}

            <NavLink
              to="/register"
              className={({ isActive }) => `child-nav-add${isActive ? ' is-active' : ''}`}
            >
              <span aria-hidden="true">+</span>
              <span>Add a child</span>
            </NavLink>
          </div>

          <div className="console-nav-group">
            <p className="console-nav-label">Account</p>
            <NavLink
              to="/payments"
              className={({ isActive }) => `console-nav-link${isActive ? ' is-active' : ''}`}
            >
              <Icon d={ICON.card} />
              <span>Payments</span>
            </NavLink>
            <NavLink
              to="/profile"
              className={({ isActive }) => `console-nav-link${isActive ? ' is-active' : ''}`}
            >
              <Icon d={ICON.user} />
              <span>My profile</span>
            </NavLink>
            <NavLink
              to="/help"
              className={({ isActive }) => `console-nav-link${isActive ? ' is-active' : ''}`}
            >
              <Icon d={ICON.help} />
              <span>Help</span>
            </NavLink>
          </div>
        </nav>

        <div className="console-sidebar-footer">
          {me?.displayName ? <span className="console-user-name">{me.displayName}</span> : null}
          {/*
            No email here: `/me` returns the principal, and the principal has no
            email field. The sign-in identity is shown on /profile, which loads
            registration.parent_guardian and has it. Adding email to /me to fill
            one line of chrome would mean widening Principal, the auth
            middleware type and the /me contract — not worth it.
          */}
          <button className="link-muted console-signout" type="button" onClick={logout}>
            Sign out
          </button>
          <ConsoleSidebarEndorsement />
        </div>
      </aside>

      <div className="console-scrim" role="presentation" onClick={() => setDrawerOpen(false)} />

      <div className="console-main">
        <div className="console-topbar">
          <div className="console-topbar-left">
            <button
              className="console-menu-btn"
              type="button"
              aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={drawerOpen}
              aria-controls="parent-nav"
              onClick={() => setDrawerOpen((open) => !open)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
            {title ? <h1>{title}</h1> : null}
          </div>
          {headerActions ? <div className="btn-row">{headerActions}</div> : null}
        </div>

        <div className="console-content">{children}</div>
      </div>
    </div>
  );
}
